/**
 * Finance route factory. The orchestrator spreads these under the authed route:
 *
 *   children: [...financeRoutes(authedRoute)]
 *
 * Pages stay router-free (props in, callbacks out); this file is the only place
 * finance code touches TanStack Router, and the only place a permission becomes
 * a rendered-or-not decision for a whole screen.
 */
import {
  createRoute,
  useNavigate,
  useParams,
  useSearch,
  type AnyRoute,
} from "@tanstack/react-router";

import { useSession } from "@/lib/session/use-session";

import { FINANCE_PERMISSIONS as P, FinancePermissionBoundary } from "./permissions";
import { AgingReportsPage } from "./pages/aging-reports-page";
import { PeriodEndPage } from "./pages/period-end-page";
import { AssetClassesPage } from "./pages/asset-classes-page";
import { BankAccountsPage } from "./pages/bank-accounts-page";
import { BankReconciliationPage } from "./pages/bank-reconciliation-page";
import { BankRulesPage } from "./pages/bank-rules-page";
import { CollectionsPage } from "./pages/collections-page";
import { StatementLayoutsPage } from "./pages/statement-layouts-page";
import { ReconciliationsPage } from "./pages/reconciliations-page";
import { StatementsPage } from "./pages/statements-page";
import { BankStatementsPage } from "./pages/bank-statements-page";
import { CustomerInvoiceNewPage } from "./pages/customer-invoice-new-page";
import { CustomerInvoicePage } from "./pages/customer-invoice-page";
import { CustomerInvoicesPage } from "./pages/customer-invoices-page";
import { CustomerReceiptNewPage } from "./pages/customer-receipt-new-page";
import { CustomerReceiptPage } from "./pages/customer-receipt-page";
import { DepreciationRunsPage } from "./pages/depreciation-runs-page";
import { FixedAssetPage } from "./pages/fixed-asset-page";
import { FixedAssetsPage } from "./pages/fixed-assets-page";
import { GatewayPage } from "./pages/gateway-page";
import { PaymentRunPage } from "./pages/payment-run-page";
import { PaymentRunsPage } from "./pages/payment-runs-page";
import { PeriodClosePage } from "./pages/period-close-page";
import { SupplierInvoiceNewPage } from "./pages/supplier-invoice-new-page";
import { SupplierInvoicePage } from "./pages/supplier-invoice-page";
import { SupplierInvoicesPage } from "./pages/supplier-invoices-page";
import { TaxCodesPage } from "./pages/tax-codes-page";

export interface ReconciliationSearch {
  /** `?statement=` deep link from the statement list into its own lines. */
  statement?: string;
}

/** Absent keys are OMITTED (never explicit undefined) per the router house rules. */
function pickString(source: Record<string, unknown>, key: string): { [k: string]: string } {
  const value = source[key];
  return typeof value === "string" && value !== "" ? { [key]: value } : {};
}

function useFinanceNav() {
  const navigate = useNavigate();
  return {
    supplierInvoices: () => void navigate({ to: "/finance/supplier-invoices" } as never),
    newSupplierInvoice: () => void navigate({ to: "/finance/supplier-invoices/new" } as never),
    supplierInvoice: (supplierInvoiceId: string) =>
      void navigate({
        to: "/finance/supplier-invoices/$supplierInvoiceId",
        params: { supplierInvoiceId },
      } as never),
    customerInvoices: () => void navigate({ to: "/finance/customer-invoices" } as never),
    newCustomerInvoice: () => void navigate({ to: "/finance/customer-invoices/new" } as never),
    customerInvoice: (invoiceId: string) =>
      void navigate({
        to: "/finance/customer-invoices/$invoiceId",
        params: { invoiceId },
      } as never),
    newCustomerReceipt: () => void navigate({ to: "/finance/customer-receipts/new" } as never),
    customerReceipt: (receiptId: string) =>
      void navigate({
        to: "/finance/customer-receipts/$receiptId",
        params: { receiptId },
      } as never),
    paymentRuns: () => void navigate({ to: "/finance/payment-runs" } as never),
    paymentRun: (paymentRunId: string) =>
      void navigate({
        to: "/finance/payment-runs/$paymentRunId",
        params: { paymentRunId },
      } as never),
    reconciliation: (statementFileId?: string) =>
      void navigate({
        to: "/finance/bank-reconciliation",
        search: statementFileId === undefined ? {} : { statement: statementFileId },
      } as never),
    fixedAssets: () => void navigate({ to: "/finance/fixed-assets" } as never),
    fixedAsset: (fixedAssetId: string) =>
      void navigate({
        to: "/finance/fixed-assets/$fixedAssetId",
        params: { fixedAssetId },
      } as never),
  };
}

function TaxCodesRoute() {
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.taxCodeRead}>
      <TaxCodesPage
        canCreate={hasPermission(P.taxCodeCreate)}
        canUpdate={hasPermission(P.taxCodeUpdate)}
        canReadAccounts={hasPermission(P.ledgerAccountRead)}
      />
    </FinancePermissionBoundary>
  );
}

function SupplierInvoicesRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.supplierInvoiceRead}>
      <SupplierInvoicesPage
        canCapture={hasPermission(P.supplierInvoiceCreate)}
        canReadParties={hasPermission(P.partyRead)}
        onOpenInvoice={nav.supplierInvoice}
        onCaptureInvoice={nav.newSupplierInvoice}
      />
    </FinancePermissionBoundary>
  );
}

function SupplierInvoiceNewRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.supplierInvoiceCreate}>
      <SupplierInvoiceNewPage
        canReadParties={hasPermission(P.partyRead)}
        canReadAccounts={hasPermission(P.ledgerAccountRead)}
        canReadTaxCodes={hasPermission(P.taxCodeRead)}
        // Coding a line against a receipt reads the purchasing collections.
        canReadPurchasing={hasPermission("purchasing.purchase_order.read")}
        onCaptured={nav.supplierInvoice}
        onCancel={nav.supplierInvoices}
      />
    </FinancePermissionBoundary>
  );
}

function SupplierInvoiceRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { supplierInvoiceId?: string };
  return (
    <FinancePermissionBoundary permission={P.supplierInvoiceRead}>
      <SupplierInvoicePage
        key={params.supplierInvoiceId}
        supplierInvoiceId={params.supplierInvoiceId}
        canMatch={hasPermission(P.supplierInvoiceMatch)}
        canPost={hasPermission(P.supplierInvoicePost)}
        canResolve={hasPermission(P.supplierInvoiceResolveDiscrepancy)}
        canOverride={hasPermission(P.supplierInvoiceOverrideDiscrepancy)}
        canReadParties={hasPermission(P.partyRead)}
        onBack={nav.supplierInvoices}
      />
    </FinancePermissionBoundary>
  );
}

function CustomerInvoicesRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.customerInvoiceRead}>
      <CustomerInvoicesPage
        canCreate={hasPermission(P.customerInvoiceCreate)}
        canReadParties={hasPermission(P.partyRead)}
        onOpenInvoice={nav.customerInvoice}
        onCreateInvoice={nav.newCustomerInvoice}
      />
    </FinancePermissionBoundary>
  );
}

function CustomerInvoiceNewRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  // `?shipment=` is how the warehouse hands an operator to finance: the
  // invoice then cites what it bills instead of relying on memory.
  const search = useSearch({ strict: false }) as { shipment?: string };
  return (
    <FinancePermissionBoundary permission={P.customerInvoiceCreate}>
      <CustomerInvoiceNewPage
        canReadParties={hasPermission(P.partyRead)}
        canReadAccounts={hasPermission(P.ledgerAccountRead)}
        canReadTaxCodes={hasPermission(P.taxCodeRead)}
        shipmentId={search.shipment}
        onCreated={nav.customerInvoice}
        onCancel={nav.customerInvoices}
      />
    </FinancePermissionBoundary>
  );
}

function CustomerInvoiceRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { invoiceId?: string };
  return (
    <FinancePermissionBoundary permission={P.customerInvoiceRead}>
      <CustomerInvoicePage
        key={params.invoiceId}
        invoiceId={params.invoiceId}
        canFinalize={hasPermission(P.customerInvoiceFinalize)}
        canCreateCreditNote={hasPermission(P.creditNoteCreate)}
        canFinalizeCreditNote={hasPermission(P.creditNoteFinalize)}
        canReadParties={hasPermission(P.partyRead)}
        onBack={nav.customerInvoices}
      />
    </FinancePermissionBoundary>
  );
}

function CustomerReceiptNewRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.customerReceiptCreate}>
      <CustomerReceiptNewPage
        canReadParties={hasPermission(P.partyRead)}
        canReadBankAccounts={hasPermission(P.bankAccountRead)}
        onCreated={nav.customerReceipt}
        onCancel={nav.customerInvoices}
      />
    </FinancePermissionBoundary>
  );
}

function CustomerReceiptRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { receiptId?: string };
  return (
    <FinancePermissionBoundary permission={P.customerReceiptRead}>
      <CustomerReceiptPage
        key={params.receiptId}
        receiptId={params.receiptId}
        canAllocate={hasPermission(P.customerReceiptAllocate)}
        canReadInvoices={hasPermission(P.customerInvoiceRead)}
        canReadParties={hasPermission(P.partyRead)}
        onBack={nav.customerInvoices}
        onOpenInvoice={nav.customerInvoice}
      />
    </FinancePermissionBoundary>
  );
}

function PaymentRunsRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.paymentRunRead}>
      <PaymentRunsPage
        canCreate={hasPermission(P.paymentRunCreate)}
        canReadInvoices={hasPermission(P.supplierInvoiceRead)}
        canReadParties={hasPermission(P.partyRead)}
        onProposed={nav.paymentRun}
      />
    </FinancePermissionBoundary>
  );
}

function PaymentRunRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { paymentRunId?: string };
  return (
    <FinancePermissionBoundary permission={P.paymentRunRead}>
      <PaymentRunPage
        key={params.paymentRunId}
        paymentRunId={params.paymentRunId}
        canSubmit={hasPermission(P.paymentRunSubmit)}
        canPost={hasPermission(P.paymentRunPost)}
        canExport={hasPermission(P.paymentRunExport)}
        canReadParties={hasPermission(P.partyRead)}
        onBack={nav.paymentRuns}
      />
    </FinancePermissionBoundary>
  );
}

function BankAccountsRoute() {
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.bankAccountRead}>
      <BankAccountsPage
        canCreate={hasPermission(P.bankAccountCreate)}
        canUpdate={hasPermission(P.bankAccountUpdate)}
        canReadAccounts={hasPermission(P.ledgerAccountRead)}
      />
    </FinancePermissionBoundary>
  );
}

function BankStatementsRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.bankStatementRead}>
      <BankStatementsPage
        canImport={hasPermission(P.bankStatementImport) && hasPermission(P.bankAccountRead)}
        canReadProfiles={hasPermission(P.bankRuleRead)}
        onOpenStatementLines={nav.reconciliation}
      />
    </FinancePermissionBoundary>
  );
}

function BankReconciliationRoute() {
  const { hasPermission } = useSession();
  const search = useSearch({ strict: false }) as ReconciliationSearch;
  return (
    <FinancePermissionBoundary permission={P.bankStatementRead}>
      <BankReconciliationPage
        initialStatementFileId={search.statement}
        canMatch={hasPermission(P.bankStatementMatch)}
        canReconcile={hasPermission(P.bankStatementReconcile)}
      />
    </FinancePermissionBoundary>
  );
}

function FixedAssetsRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.fixedAssetRead}>
      <FixedAssetsPage
        canCapitalize={hasPermission(P.fixedAssetCreate)}
        canReadClasses={hasPermission(P.assetClassRead)}
        onOpenAsset={nav.fixedAsset}
      />
    </FinancePermissionBoundary>
  );
}

function FixedAssetRoute() {
  const nav = useFinanceNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { fixedAssetId?: string };
  return (
    <FinancePermissionBoundary permission={P.fixedAssetRead}>
      <FixedAssetPage
        key={params.fixedAssetId}
        fixedAssetId={params.fixedAssetId}
        canRevise={hasPermission(P.fixedAssetUpdate)}
        canDispose={hasPermission(P.fixedAssetDispose)}
        canReadClasses={hasPermission(P.assetClassRead)}
        canReadFiscalPeriods={hasPermission(P.ledgerFiscalPeriodRead)}
        onBack={nav.fixedAssets}
      />
    </FinancePermissionBoundary>
  );
}

function AssetClassesRoute() {
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.assetClassRead}>
      <AssetClassesPage
        canCreate={hasPermission(P.assetClassCreate)}
        canUpdate={hasPermission(P.assetClassUpdate)}
        canReadAccounts={hasPermission(P.ledgerAccountRead)}
      />
    </FinancePermissionBoundary>
  );
}

function DepreciationRunsRoute() {
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.depreciationRunRead}>
      <DepreciationRunsPage
        canExecute={hasPermission(P.depreciationRunExecute)}
        canReadFiscalPeriods={hasPermission(P.ledgerFiscalPeriodRead)}
        canReadClasses={hasPermission(P.assetClassRead)}
      />
    </FinancePermissionBoundary>
  );
}

function PeriodCloseRoute() {
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.periodCloseRead}>
      <PeriodClosePage
        canRunChecks={hasPermission(P.periodCloseRunChecks)}
        canSignOff={hasPermission(P.periodCloseSignOff)}
        canTransition={hasPermission(P.periodCloseTransition)}
        canReopen={hasPermission(P.periodCloseReopen)}
        canReadYearEnd={hasPermission(P.yearEndCloseRead)}
        canExecuteYearEnd={hasPermission(P.yearEndCloseExecute)}
        canReadFiscalPeriods={hasPermission(P.ledgerFiscalPeriodRead)}
        canReadFiscalYears={hasPermission(P.ledgerFiscalCalendarRead)}
      />
    </FinancePermissionBoundary>
  );
}

function GatewayRoute() {
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.gatewayPaymentRead}>
      <GatewayPage
        canResolve={hasPermission(P.gatewayPaymentResolve)}
        canReplay={hasPermission(P.gatewayPaymentIngest)}
        // Reconciling a settlement is a bank-reconcile act, per the contract.
        canReconcilePayout={hasPermission(P.bankStatementReconcile)}
      />
    </FinancePermissionBoundary>
  );
}

function CollectionsRoute() {
  const { hasPermission, company } = useSession();
  return (
    <FinancePermissionBoundary permission={P.dunningRead}>
      <CollectionsPage
        baseCurrency={company?.base_currency ?? "SAR"}
        canConfigure={hasPermission(P.dunningConfigure)}
        canRunDunning={hasPermission(P.dunningRun)}
        canRunStatements={hasPermission(P.customerStatementRun)}
        canReadStatements={hasPermission(P.customerStatementRead)}
      />
    </FinancePermissionBoundary>
  );
}

function ProofsRoute() {
  const { hasPermission, company } = useSession();
  return (
    <FinancePermissionBoundary permission={P.taxReturnRead}>
      <ReconciliationsPage
        baseCurrency={company?.base_currency ?? "SAR"}
        canReadTaxReturn={hasPermission(P.taxReturnRead)}
        canReadRegister={hasPermission(P.fixedAssetRead)}
        canReadGRNI={hasPermission(P.grniRead)}
      />
    </FinancePermissionBoundary>
  );
}

function StatementsRoute() {
  const { company } = useSession();
  return (
    <FinancePermissionBoundary permission={P.statementRead}>
      <StatementsPage baseCurrency={company?.base_currency ?? ""} />
    </FinancePermissionBoundary>
  );
}

function StatementLayoutsRoute() {
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.statementLayoutRead}>
      <StatementLayoutsPage
        canCreate={hasPermission(P.statementLayoutCreate)}
        canUpdate={hasPermission(P.statementLayoutUpdate)}
      />
    </FinancePermissionBoundary>
  );
}

function BankRulesRoute() {
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.bankRuleRead}>
      <BankRulesPage canConfigure={hasPermission(P.bankRuleConfigure)} />
    </FinancePermissionBoundary>
  );
}

function PeriodEndRoute() {
  const { hasPermission } = useSession();
  return (
    <FinancePermissionBoundary permission={P.recurringJournalRead}>
      <PeriodEndPage
        canReadRecurring={hasPermission(P.recurringJournalRead)}
        canMaterialize={hasPermission(P.recurringJournalMaterialize)}
        canWriteRecurring={
          hasPermission(P.recurringJournalCreate) && hasPermission(P.recurringJournalUpdate)
        }
        canReadFx={hasPermission(P.fxRevaluationRead)}
        canRunFx={hasPermission(P.fxRevaluationExecute)}
        canCloseYear={hasPermission(P.yearEndCloseExecute)}
      />
    </FinancePermissionBoundary>
  );
}

function AgingReportsRoute() {
  const { hasPermission, company } = useSession();
  const canReadAR = hasPermission(P.arAgingRead);
  const canReadAP = hasPermission(P.apAgingRead);
  // The page is two reports behind one filter bar; either grant opens it.
  return (
    <FinancePermissionBoundary permission={canReadAR ? P.arAgingRead : P.apAgingRead}>
      <AgingReportsPage
        baseCurrency={company?.base_currency ?? ""}
        canReadAR={canReadAR}
        canReadAP={canReadAP}
        canReadParties={hasPermission(P.partyRead)}
      />
    </FinancePermissionBoundary>
  );
}

/**
 * Route factory per the feature-module contract: every finance page under the
 * caller's (authed) parent route.
 */
export function financeRoutes(parent: AnyRoute): AnyRoute[] {
  return [
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/tax-codes",
      component: TaxCodesRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/supplier-invoices",
      component: SupplierInvoicesRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/supplier-invoices/new",
      component: SupplierInvoiceNewRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/supplier-invoices/$supplierInvoiceId",
      component: SupplierInvoiceRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/customer-invoices",
      component: CustomerInvoicesRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/customer-invoices/new",
      validateSearch: (search: Record<string, unknown>): { shipment?: string } => ({
        ...pickString(search, "shipment"),
      }),
      component: CustomerInvoiceNewRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/customer-invoices/$invoiceId",
      component: CustomerInvoiceRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/customer-receipts/new",
      component: CustomerReceiptNewRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/customer-receipts/$receiptId",
      component: CustomerReceiptRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/payment-runs",
      component: PaymentRunsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/payment-runs/$paymentRunId",
      component: PaymentRunRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/bank-accounts",
      component: BankAccountsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/bank-statements",
      component: BankStatementsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/bank-reconciliation",
      validateSearch: (search: Record<string, unknown>): ReconciliationSearch => ({
        ...pickString(search, "statement"),
      }),
      component: BankReconciliationRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/bank-rules",
      component: BankRulesRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/proofs",
      component: ProofsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/statements",
      component: StatementsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/statement-layouts",
      component: StatementLayoutsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/collections",
      component: CollectionsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/fixed-assets",
      component: FixedAssetsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/fixed-assets/$fixedAssetId",
      component: FixedAssetRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/asset-classes",
      component: AssetClassesRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/depreciation-runs",
      component: DepreciationRunsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/period-end",
      component: PeriodEndRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/period-close",
      component: PeriodCloseRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/gateway",
      component: GatewayRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/finance/reports/aging",
      component: AgingReportsRoute,
    }),
  ] as AnyRoute[];
}
